import { useCallback, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CognitoUserPool, CognitoUser } from "amazon-cognito-identity-js";
import { LoginUserContext } from "../providers/LoginUserProvider";
import { useMessage } from "./useMessage";
import awsConfiguration from "../awsConfiguration";
import { getPasswordValidityDays } from "../config";

// デバッグ用: 強制的に期限切れにするフラグ
const FORCE_PASSWORD_EXPIRED = false; // テスト用（本番ではfalseに戻す）

// 一時的に有効期限チェックを無効化（属性の権限問題を解決するまで）
const DISABLE_PASSWORD_EXPIRY_CHECK = false; // 権限問題が解決されたので有効化

// テスト用: 短い有効期限に変更する場合は以下を有効化
// const OVERRIDE_PASSWORD_VALIDITY_DAYS = 0.01; // 約14.4分で期限切れ

const userPool = new CognitoUserPool({
  UserPoolId: awsConfiguration.UserPoolId,
  ClientId: awsConfiguration.ClientId,
});

export const usePasswordValidation = () => {
  const authCtx = useContext(LoginUserContext);
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const [isPasswordExpiredState, setIsPasswordExpiredState] = useState<boolean>(false);

  // パスワード最終変更日を取得する関数
  const getPasswordLastChanged = useCallback(async (username: string): Promise<Date | null> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session?.isValid()) {
          console.error('Failed to get session:', err);
          resolve(null);
          return;
        }

        cognitoUser.getUserAttributes((attrErr, attributes) => {
          if (attrErr) {
            console.error('Failed to get user attributes:', attrErr);
            resolve(null);
            return;
          }

          if (attributes) {
            const passwordChangedAttr = attributes.find(attr => attr.Name === 'custom:password_changed_at');
            if (passwordChangedAttr && passwordChangedAttr.Value) {
              try {
                const passwordChangedDate = new Date(passwordChangedAttr.Value);
                resolve(passwordChangedDate);
              } catch (parseErr) {
                console.error('Failed to parse password changed date:', parseErr);
                resolve(null);
              }
            } else {
              // パスワード変更日が設定されていない場合はnullを返す（期限切れチェックでは適切に処理）
              console.warn('Password changed date not found in user attributes');
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      });
    });
  }, []);

  // パスワード有効期限をチェックする関数
  const isPasswordExpired = useCallback(async (username: string): Promise<boolean> => {
    try {
      console.log(`DEBUG: Starting password expiry check for user: ${username}`);
      
      // 緊急対応: 一時的に無効化
      if (DISABLE_PASSWORD_EXPIRY_CHECK) {
        console.log('DEBUG: Password expiry check is temporarily disabled');
        return false;
      }

      // デバッグ用: 強制的に期限切れにする
      if (FORCE_PASSWORD_EXPIRED) {
        console.log('DEBUG: Forcing password expiration for testing');
        return true;
      }

      const lastChanged = await getPasswordLastChanged(username);
      if (!lastChanged) {
        // パスワード変更日が取得できない場合は、初回ログインまたは古いユーザーとして扱う
        // セキュリティ上、期限切れとして扱う
        console.warn('Password changed date not found - treating as expired for security');
        return true;
      }

      // パスワード有効期限を動的に取得
      const passwordValidityDays = getPasswordValidityDays();
      
      // 無期限の場合（-1 or 0 or負の値）は期限切れにしない
      if (passwordValidityDays <= 0) {
        console.log(`Password validity is unlimited (${passwordValidityDays} days) - never expires`);
        return false;
      }
      
      // テスト用のオーバーライド
      // const passwordValidityDays = OVERRIDE_PASSWORD_VALIDITY_DAYS || getPasswordValidityDays();

      const now = new Date();
      const diffInDays = (now.getTime() - lastChanged.getTime()) / (1000 * 60 * 60 * 24);
      const isExpired = diffInDays > passwordValidityDays;
      
      console.log(`Password last changed: ${lastChanged.toISOString()}, Days since: ${diffInDays.toFixed(3)}, Expired: ${isExpired}, Threshold: ${passwordValidityDays} days`);
      
      return isExpired;
    } catch (error) {
      console.error('Error checking password expiration:', error);
      return false; // エラー時は期限切れではないとして扱う
    }
  }, [getPasswordLastChanged]);

  // パスワード最終変更日を更新する関数
  const updatePasswordChangedDate = useCallback(async (username: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session?.isValid()) {
          console.error('Failed to get session for update:', err);
          resolve(false);
          return;
        }

        const attributes = [{
          Name: 'custom:password_changed_at',
          Value: new Date().toISOString()
        }];

        cognitoUser.updateAttributes(attributes, (updateErr, result) => {
          if (updateErr) {
            console.error('Failed to update password changed date:', updateErr);
            resolve(false);
          } else {
            console.log('Password changed date updated successfully');
            resolve(true);
          }
        });
      });
    });
  }, []);

  // ログイン時のパスワード有効期限チェック
  const validatePasswordOnLogin = useCallback(async () => {
    if (!authCtx.isLoggedIn || !authCtx.loginUser) {
      console.log('DEBUG: Not logged in or no login user - skipping password validation');
      return;
    }

    try {
      const username = authCtx.loginUser.userName;
      if (!username) {
        console.warn('Username not found in login user context');
        return;
      }

      console.log(`DEBUG: Checking password expiration for user: ${username}`);
      const expired = await isPasswordExpired(username);
      console.log(`DEBUG: Password expired result: ${expired}`);
      
      if (expired) {
        console.log('DEBUG: Password is expired - navigating to change password');
        // Remove the showMessage call here to avoid duplicate messages
        navigate("/change-password", { 
          state: { 
            expiredPassword: true,
            forceChange: true 
          } 
        });
      } else {
        console.log('DEBUG: Password is not expired - no action needed');
      }
    } catch (error) {
      console.error('Error during password validation on login:', error);
      // エラーが発生した場合は処理を続行（ユーザーをブロックしない）
    }
  }, [authCtx, isPasswordExpired, navigate, showMessage]);

  // パスワード有効期限状態をチェックする関数
  const checkPasswordExpiredStatus = useCallback(async (): Promise<boolean> => {
    if (!authCtx.isLoggedIn || !authCtx.loginUser) {
      return false;
    }

    try {
      const username = authCtx.loginUser.userName;
      if (!username) {
        return false;
      }

      const expired = await isPasswordExpired(username);
      setIsPasswordExpiredState(expired);
      return expired;
    } catch (error) {
      console.error('Error checking password expired status:', error);
      return false;
    }
  }, [authCtx, isPasswordExpired]);

  // パスワード有効期限状態をリセットする関数
  const resetPasswordExpiredState = useCallback(() => {
    setIsPasswordExpiredState(false);
  }, []);

  // ログイン状態が変更されたときにパスワード有効期限状態をチェック
  useEffect(() => {
    if (authCtx.isLoggedIn && authCtx.loginUser) {
      // ユーザーがログインしたら、そのユーザーのパスワード有効期限状態を新たに確認
      console.log(`DEBUG: User logged in or changed: ${authCtx.loginUser.userName} - checking password expiry status`);
      checkPasswordExpiredStatus();
    } else {
      // ログアウト時は必ず期限切れ状態をリセット
      console.log('DEBUG: User logged out - resetting password expiry state');
      setIsPasswordExpiredState(false);
    }
  }, [authCtx.isLoggedIn, authCtx.loginUser?.userName, checkPasswordExpiredStatus]);

  return {
    getPasswordLastChanged,
    isPasswordExpired,
    updatePasswordChangedDate,
    validatePasswordOnLogin,
    checkPasswordExpiredStatus,
    isPasswordExpiredState,
    resetPasswordExpiredState,
    getPasswordValidityDays
  };
};
